import React, { useMemo } from 'react';
import { Select, SelectOptionProps, SelectProps } from '@televet/kibble-ui/build/components/Select';
import { Text } from '@televet/kibble-ui/build/components/Text';
import { useWidgetStore } from 'state/useWidgetStore';
import { Flex } from '@televet/kibble-ui/build/chakra';
import { EnrollmentStatus } from 'shared/types/graphql';
import { Icon } from '@televet/kibble-ui/build/components/Icon';
import { Alert } from '@televet/kibble-ui/build/components/Alert';
import { env } from 'env';
import { TextInput, TextInputProps } from '@televet/kibble-ui/build/components/TextInput';

export interface SelectedPet {
  id: string;
  name: string;
  isEnrolled: boolean;
  planHasTeletriage: boolean;
}

interface PetPickerProps {
  selectedPet?: SelectedPet;
  handlePetSelection: (pet: SelectedPet) => void;
  inputProps?: SelectProps | TextInputProps;
  isTeletriageRequest?: boolean;
}

export const PetPicker = ({
  selectedPet,
  handlePetSelection,
  inputProps,
  isTeletriageRequest = false,
}: PetPickerProps): JSX.Element => {
  const { clinicPetParent, isPetPortal } = useWidgetStore((state) => state);

  const { pets, hasPets } = useMemo(
    () => ({
      pets: clinicPetParent?.pets,
      hasPets: !!clinicPetParent?.pets?.length,
    }),
    [clinicPetParent?.pets],
  );

  const options: SelectOptionProps[] = useMemo(
    () =>
      pets?.map((pet) => {
        const enrollments = pet.organizationPet?.carePlanEnrollments;
        const activeEnrollment = enrollments?.find((enrollment) => enrollment.status === EnrollmentStatus.Active);
        const hasTeletriageBenefit = !!activeEnrollment?.plan.planBenefits.some(
          (planBenefit) => planBenefit.benefit.type === 'Teletriage',
        );
        const isEnrolled = !!activeEnrollment;

        return {
          label: (
            <Flex alignItems="center">
              <Text>{pet.name}</Text>
              {isEnrolled && (
                <Flex color="yellow.400" ml={1} mt={0}>
                  <Icon name="starCircle" size="sm" color="currentColor" />
                </Flex>
              )}
            </Flex>
          ),
          value: pet.id,
          onSelect: (): void =>
            handlePetSelection({ id: pet.id, name: pet.name, isEnrolled, planHasTeletriage: hasTeletriageBenefit }),
        };
      }) || [],
    [pets, handlePetSelection],
  );

  const handleManualPetNameChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const name = e.target.value;
    if (name) {
      handlePetSelection({
        id: 'manual-pet-entry',
        name,
        isEnrolled: false,
        planHasTeletriage: false,
      });
    }
  };

  const handleAlertClick = (): void => {
    window.open(`${env.REACT_APP_PET_WEB_URL}/?forward-to=careplan`, '_blank');
  };

  return (
    <>
      {hasPets ? (
        <Select
          {...(inputProps as SelectProps)}
          options={options}
          placeholder="Select Pet"
          listProps={{ maxH: '180px' }}
          value={selectedPet?.id}
        />
      ) : (
        <TextInput
          {...(inputProps as TextInputProps)}
          className="Request__TextInput"
          label={'Pet Name'}
          placeholder="Enter pet's name"
          onChange={handleManualPetNameChange}
        />
      )}
      {isTeletriageRequest && !!selectedPet && (
        <>
          {!selectedPet.isEnrolled && (
            <Alert
              status={inputProps?.isInvalid ? 'error' : 'info'}
              title="This pet is not a Care member."
              description="24/7 Live Chat is only available for pets under a Care membership"
              buttonProps={{
                buttonText: 'Sign Up Now',
                buttonPosition: 'bottomLeft',
                onClick: handleAlertClick,
              }}
              hideCloseButton={true}
              mt={3}
            />
          )}
          {selectedPet.isEnrolled && !selectedPet.planHasTeletriage && (
            <Alert
              status={inputProps?.isInvalid ? 'error' : 'info'}
              title="This pet's plan doesn't include 24/7 Live Chat."
              description={
                isPetPortal
                  ? 'Contact the clinic directly with any questions/concerns.'
                  : 'Please choose a different request option.'
              }
              hideCloseButton={true}
              mt={3}
            />
          )}
        </>
      )}
    </>
  );
};
